import { useEffect, useRef, useState } from 'react';
import { isDevEnv, isTestEnv } from 'utils/common';
import { WEBSOCKET_DEV_URL, WEBSOCKET_PROD_URL, WEBSOCKET_TEST_URL } from 'constants/common';
import { getUserToken } from 'utils/localstore';
import { setLatestActiveTime, getLatestActiveTime } from 'utils/sessionStorage';

export interface TWebSocketListener {
    onOpen?: (event: Event) => void;
    onClose?: (event: CloseEvent) => void;
    onMessage?: (event: MessageEvent) => void;
    onError?: (event: Event) => void;
    autoReconnectTime?: number;
    autoCloseTime?: number;
}

export const useOpsMessageWebSocket = (options?: TWebSocketListener): [WebSocket | null] => {
    const { autoReconnectTime = 1000 * 20, autoCloseTime = 1000 * 60 * 10 } = options || {};

    const checkConnectRef = useRef<number | null>(null);

    const [url, setUrl] = useState<string>();
    const socketRef = useRef<WebSocket | null>(null);

    const doConnect = () => {
        const headers = {
            'user-sys': 'admin-server',
            token: getUserToken()!,
            'device-info': window.navigator.userAgent,
        };

        const queryString = Object.entries(headers)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        // Initialize the WebSocket connection
        socketRef.current = new WebSocket(`${url}?${queryString}`);

        const socket = socketRef.current;

        socket.onopen = (event) => {
            options?.onOpen?.(event);
        };

        socket.onclose = (event) => {
            options?.onClose?.(event);
        };

        socket.onmessage = (event) => {
            options?.onMessage?.(event);
        };

        socket.onerror = (event) => {
            options?.onError?.(event);
        };
    };

    useEffect(() => {
        if (isDevEnv()) {
            setUrl(WEBSOCKET_DEV_URL);
        } else if (isTestEnv()) {
            setUrl(WEBSOCKET_TEST_URL);
        } else {
            setUrl(WEBSOCKET_PROD_URL);
        }
    }, []);

    useEffect(() => {
        if (!url) {
            return;
        }
        doConnect();
        setLatestActiveTime(new Date().getTime());

        checkConnectRef.current = window.setInterval(() => {
            const latestActiceTime = getLatestActiveTime();
            const nowTime = new Date().getTime();
            const dif = nowTime - latestActiceTime;
            // console.log({
            //   latestActiceTime,
            //   nowTime,
            //   dif,
            // })
            if (socketRef.current && socketRef.current.readyState === 1) {
                if (dif > autoCloseTime) {
                    socketRef.current?.close();
                }
            } else if (dif < autoCloseTime) {
                doConnect();
            }
        }, autoReconnectTime);

        const handleClick = () => {
            setLatestActiveTime(new Date().getTime());
        };
        window.addEventListener('click', handleClick);
        window.addEventListener('touchend', handleClick);

        return () => {
            if (checkConnectRef.current) {
                window.clearInterval(checkConnectRef.current);
            }
            window.removeEventListener('click', handleClick);
            window.addEventListener('touchend', handleClick);

            if (socketRef.current) {
                socketRef.current.close();
            }
        };
    }, [url]);

    return [socketRef.current];
};
